import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Divider
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { bulkCreateTeacherAbsences, fetchTeacherAbsences } from '../../slices/staffSlice';
import { useToast } from '../../context/ToastContext';

const TeacherAbsenceDialog = ({ open, onClose, selectedDate }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  
  // Get teachers from Redux
  const { filtered: staffList } = useSelector((state) => state.staff);
  const { teacherAbsences, absenceLoading } = useSelector((state) => state.staff);
  
  // Filter only teachers
  const teachers = staffList.filter(s => s.is_teacher && !s.is_archived);
  
  // State
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [reason, setReason] = useState('other');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Fetch existing absences for the selected date
  useEffect(() => {
    if (open && selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      dispatch(fetchTeacherAbsences({ absence_date: dateStr }));
    }
  }, [open, selectedDate, dispatch]);
  
  // Update selected teachers based on existing absences
  useEffect(() => {
    if (open && teacherAbsences.length > 0) {
      const absentTeacherIds = teacherAbsences.map(a => a.teacher);
      setSelectedTeachers(absentTeacherIds);
    } else if (open) {
      setSelectedTeachers([]);
    }
  }, [open, teacherAbsences]);
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTeachers([]);
      setReason('other');
      setNotes('');
    }
  }, [open]);
  
  const handleTeacherToggle = (teacherId) => {
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };
  
  const handleSave = async () => {
    if (selectedTeachers.length === 0) {
      showToast('يرجى اختيار معلم واحد على الأقل', 'warning');
      return;
    }
    
    if (!selectedDate) {
      showToast('يرجى اختيار تاريخ', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const result = await dispatch(bulkCreateTeacherAbsences({
        absence_date: dateStr,
        teacher_ids: selectedTeachers,
        reason: reason,
        notes: notes
      }));
      
      if (result.type.endsWith('/fulfilled')) {
        showToast(`تم تسجيل غياب ${result.payload.created_count} معلم بنجاح`, 'success');
        // Refresh absences
        dispatch(fetchTeacherAbsences({ absence_date: dateStr }));
        onClose();
      } else {
        showToast('حدث خطأ أثناء تسجيل الغياب', 'error');
      }
    } catch (error) {
      console.error('Error saving absences:', error);
      showToast('حدث خطأ أثناء تسجيل الغياب', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const reasonOptions = [
    { value: 'sick', label: 'مرض' },
    { value: 'personal', label: 'أمر شخصي' },
    { value: 'emergency', label: 'طوارئ' },
    { value: 'vacation', label: 'إجازة' },
    { value: 'other', label: 'أخرى' },
  ];
  
  const getDateDisplay = () => {
    if (!selectedDate) return '';
    // Format as DD/MM/YYYY
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const year = selectedDate.getFullYear();
    return `${day}/${month}/${year}`;
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, direction: 'rtl' } }}
    >
      <DialogTitle>
        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
          <PersonIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            تسجيل غياب المعلمين
          </Typography>
        </Stack>
        {selectedDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {getDateDisplay()}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="info">
            اختر المعلمين الغائبين لهذا اليوم. سيتم عرض حصصهم باللون الأحمر في الجدول.
          </Alert>
          
          <FormControl fullWidth>
            <InputLabel>سبب الغياب</InputLabel>
            <Select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              label="سبب الغياب"
            >
              {reasonOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="ملاحظات (اختياري)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          
          <Divider />
          
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              المعلمون ({selectedTeachers.length} محدد)
            </Typography>
            <Box
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                mt: 1
              }}
            >
              {teachers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  لا توجد معلمين متاحين
                </Typography>
              ) : (
                <FormGroup>
                  {teachers.map((teacher) => {
                    const isSelected = selectedTeachers.includes(teacher.id);
                    const isAbsent = teacherAbsences.some(a => a.teacher === teacher.id);
                    
                    return (
                      <FormControlLabel
                        key={teacher.id}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleTeacherToggle(teacher.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">
                              {teacher.first_name} {teacher.last_name}
                            </Typography>
                            {isAbsent && (
                              <Chip
                                label="غائب"
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Stack>
                        }
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: isSelected ? 'primary.light' : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      />
                    );
                  })}
                </FormGroup>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading || selectedTeachers.length === 0}
        >
          حفظ ({selectedTeachers.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeacherAbsenceDialog;
